import type { Children } from '@lc/libreact'
import type { PriorityExecutor } from '@lc/librunner'
import {
	createContext,
	useContext,
	useEffect,
	useState,
	type ComponentType,
} from 'react'
import { useLoad } from './hooks/load/useLoad'
import { load, makeLoader } from './stores/load/load'
import { clearLoadingState } from './stores/loading'
import type { Store } from './stores/store'

export type RegistryModule<T> = {
	chunk: string
	load: () => Promise<{ default: T }>
	loaded: boolean
	value: T
}
export type RegistryListener = (
	name: string,
	module: RegistryModule<unknown>,
) => void

const registry = new Map<string, RegistryModule<unknown>>()
if (typeof window !== 'undefined') {
	Object.assign(window, { registry })
}

export const RegistryContext = createContext<RegistryListener | null>(null)
RegistryContext.displayName = 'RegistryContext'

function getLevelFromProps(props: unknown): number {
	if (
		typeof props === 'object' &&
		props &&
		'level' in props &&
		typeof props.level === 'number'
	)
		return props.level
	return 0
}

export function declareComponent<T>(
	name: string,
	chunk: string,
	loadFn: () => Promise<{ default: ComponentType<T> }>,
	Fallback: ComponentType<T>,
): ComponentType<T & { level?: number }> & {
	preload: (params: {
		level: number
		executor: PriorityExecutor
		store: Store
	}) => Promise<void> & {
		id: string
		status: 'pending' | 'loading' | 'loaded'
	}
} {
	const module: RegistryModule<ComponentType<T>> = {
		chunk,
		load: loadFn,
		loaded: false,
		value: Fallback,
	}
	registry.set(name, module)

	const loader = makeLoader({
		id: `module:${name}`,
		loader: async () => {
			module.value = await loadFn().then(x => x.default)
			module.loaded = true
			console.log(`[registry] Module ${name} loaded from render`)
		},
		check: () => module.loaded,
	})

	return Object.assign(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		function ModuleWrapper(props: any) {
			// load the module at the required level
			const level = getLevelFromProps(props)
			const { status } = useLoad(loader, { level })

			// register the module in the context, in the SSR case
			const ctx = useContext(RegistryContext)
			ctx?.(name, module)

			// actually render the component
			// we do this in useEffect and useState, because react complains otherwise
			const [children, setChildren] = useState<Children>(() => {
				if (!module.loaded) return <Fallback {...props} />

				const Module = module.value
				return <Module {...props} />
			})
			useEffect(() => {
				if (status !== 'loaded') return

				const Module = module.value
				// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
				setChildren(<Module {...props} />)
			}, [status, props])
			return children
		},
		{
			preload: ({
				level,
				executor,
				store,
			}: {
				level: number
				executor: PriorityExecutor
				store: Store
			}) => {
				return load({
					executor,
					store,
					level,
					id: `module:${name}`,
					loader: async () => {
						module.value = await loadFn().then(x => x.default)
						module.loaded = true
						console.log(
							`[registry] Module ${name} loaded from Component.preload`,
						)
					},
					check: () => module.loaded,
				})
			},
		},
	)
}

export function preloadModule(
	store: Store,
	executor: PriorityExecutor,
	name: string,
	level = 4,
): Promise<void> & {
	id: string
	status: 'pending' | 'loading' | 'loaded'
} {
	return load({
		store,
		executor,
		id: `module:${name}`,
		level,
		loader: async () => {
			const module = registry.get(name)
			if (!module) throw new Error(`Module ${name} not registered`)
			if (module.loaded) return
			module.value = await module.load().then(x => x.default)
			module.loaded = true
			console.log(`[registry] Module ${name} loaded from preloadModule`)
		},
	})
}

export function preloadModules(
	store: Store,
	executor: PriorityExecutor,
	level = 4,
): Promise<void> {
	return Promise.all(
		listModules().map(module =>
			preloadModule(store, executor, module, level),
		),
	).then(() => void 0)
}

export async function reloadModules(store?: Store): Promise<void> {
	for (const [name, module] of registry) {
		if (!module.loaded) continue
		store?.dispatch(clearLoadingState(`module:${name}`))
		module.value = await module.load().then(x => x.default)
		console.log(`{registry] Reloaded module ${name}`)
	}
}

export function listModules(): string[] {
	return [...registry.keys()]
}
